import { useState } from "react";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import { useOrg } from "../org";
import { fmtInChurch } from "./text";
import { VoicePlayback, VoiceRecorder, VoiceReview, VoiceStatus, discardVoice, saveVoice, useVoiceProgress } from "./Voice";

const when = (iso) => fmtInChurch(iso, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

const Note = ({ n, onSaved, onArchived }) => {
  const { api, membership } = useOrg();
  const [edit, setEdit] = useState(null);
  const canChange = n.mine || (membership.role === "owner" && membership.confidential_access);
  const save = () => api(`/confidential/${n.id}`, { method: "PATCH", body: { content: edit } }).then((u) => { setEdit(null); toast.success("Note updated"); onSaved(u); }).catch((e) => toast.error(e.message));
  const archive = () => window.confirm("Archive this confidential note?") && api(`/confidential/${n.id}`, { method: "DELETE" }).then(() => { toast("Note archived"); onArchived(n.id); }).catch((e) => toast.error(e.message));
  return (
    <div className="py-4" data-testid={`conf-note-${n.id}`}>
      {edit === null ? <p className="t-body whitespace-pre-wrap">{n.content}</p>
        : <textarea value={edit} onChange={(e) => setEdit(e.target.value)} rows={4} className="field p-3" data-testid={`conf-edit-input-${n.id}`} />}
      <p className="t-meta mt-2">{n.author_name} · {when(n.created_at)}{n.updated_at ? ` · edited ${when(n.updated_at)}` : ""}</p>
      {n.voice && <VoicePlayback voice={n.voice} />}
      {canChange && (
        <div className="flex gap-2 mt-2">
          {edit === null ? <button onClick={() => setEdit(n.content)} className="btn btn-quiet btn-sm" data-testid={`conf-edit-${n.id}`}>Edit</button>
            : <button disabled={!edit.trim()} onClick={save} className="btn btn-primary btn-sm" data-testid={`conf-save-${n.id}`}>Save</button>}
          {edit === null ? <button onClick={archive} className="btn btn-quiet btn-sm" data-testid={`conf-archive-${n.id}`}>Archive</button>
            : <button onClick={() => setEdit(null)} className="btn btn-quiet btn-sm">Cancel</button>}
        </div>
      )}
    </div>
  );
};

export const Confidential = ({ p }) => {
  const { api, membership } = useOrg();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [voice, setVoice] = useState(null);
  const load = () => api(`/people/${p.id}/confidential`).then(setData).catch((e) => toast.error(e.message));
  const add = async () => {
    try {
      const n = await api(`/people/${p.id}/confidential`, { method: "POST", body: { content: text } });
      if (voice) await saveVoice(api, voice, { confidential_note_id: n.id }).catch((e) => toast.error(`Note saved, but the voice note wasn't attached: ${e.message}`));
      setText(""); setVoice(null); toast.success("Confidential note saved");
      load();
    } catch (e) { toast.error(e.message); }
  };
  const gotVoice = (v) => { setVoice(v); if (v.summary) setText((t) => t || v.summary); };
  useVoiceProgress(voice, gotVoice);
  const saved = (u) => setData((d) => ({ ...d, notes: d.notes.map((x) => (x.id === u.id ? u : x)) }));
  const archived = (id) => setData((d) => ({ ...d, notes: d.notes.filter((x) => x.id !== id) }));
  return (
    <section className="mt-8 rounded-[20px] border border-line bg-private-soft p-5 sm:p-6" data-testid="person-confidential">
      <div className="flex items-start gap-3">
        <Lock size={18} className="text-private mt-1 shrink-0" />
        <div className="min-w-0 flex-1">
          <h2 className="t-heading-sm">Confidential</h2>
          <p className="t-meta mt-1">Separate from {p.preferred_name || p.first_name}'s Story. Seen only by the author and pastors with Confidential Access. Opening these notes is recorded.</p>
        </div>
      </div>
      {!data ? (
        <button onClick={load} className="btn btn-outline btn-sm mt-4" data-testid="conf-open-btn">Open confidential notes</button>
      ) : (
        <div className="mt-4">
          <div className="rule-list border-y border-line" data-testid="conf-notes">
            {data.notes.map((n) => <Note key={n.id} n={n} onSaved={saved} onArchived={archived} />)}
            {!data.notes.length && <p className="py-4 t-meta" data-testid="conf-empty">{data.full_access ? "No confidential notes yet." : "You haven't written any confidential notes for this person."}</p>}
          </div>
          {membership.confidential_access && (!voice ? <VoiceRecorder person={p} confidential onResult={gotVoice} /> : (
            <div className="surface p-4 mt-4" data-testid="conf-voice-attached">
              <div className="flex items-center justify-between"><p className="t-body font-medium">Voice note attached</p><button onClick={() => { discardVoice(api, voice); setVoice(null); }} className="text-sm text-ink-soft underline underline-offset-4" data-testid="conf-voice-remove">Remove</button></div>
              <VoiceStatus v={voice} onRetry={gotVoice} />
              <VoiceReview v={voice} setV={setVoice} allowed={[]} />
            </div>
          ))}
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Private pastoral note" className="field p-3 mt-4" data-testid="conf-new-input" />
          <button disabled={!text.trim()} onClick={add} className="btn btn-primary btn-sm mt-3" data-testid="conf-add-btn">Save confidential note</button>
        </div>
      )}
    </section>
  );
};
